'use client'

import { cloneElement, type FC, type ReactElement, type ReactNode, useRef } from 'react'
import { AlertTriangle, ExternalLink, Info, List, Printer } from 'lucide-react'
import { Box, Row } from '@christiankaindl/lyts'
import * as styles from './BottomBar.css'
import Button from '@/components/Button/Button'
import { MenuItem, MenuList } from '@/components/Menu'
import { create } from 'zustand'
import useEvent from '@/hooks/useEvent'
import { infoOverlayStore } from '@/components/Norm/Utils'
import { Overlay, OverlayContent } from '@/components/Overlay'
import { DialogDisclosure } from '@ariakit/react'
import { TocContext } from '../Sidebar/Toc'

export const useSidebarOverlayStore = create<{ isOpen: boolean, setIsOpen: (isOpen: boolean) => void }>((set) => ({
  isOpen: false,
  setIsOpen (isOpen) {
    set({ isOpen })
  },
}))

interface BottomBarProps {
  title: string
  menu?: ReactNode
  children: ReactElement<any>
  normId: string
  abbrNr?: number
  showSearch?: boolean
  noToc?: boolean
  headerButtons?: ReactElement<any>
}
const BottomBar: FC<BottomBarProps> = function NavMenu (props) {
  const { isOpen, setIsOpen } = useSidebarOverlayStore()

  const onSelect = useEvent(() => {
    setIsOpen(false)
  })

  const parentRef = useRef(null)

  return (
    <Overlay open={isOpen} setOpen={(isOpen) => { setIsOpen(isOpen) }}>
      <Row xAlign='space-around' className={styles.trigger}>
        {!props.noToc && (
          <DialogDisclosure render={<Button icon={List} title='Inhaltsverzeichnis' variant='flat' size='large' />} />
        )}
        {props.headerButtons && cloneElement(props.headerButtons, { variant: 'flat' })}
        <Button onClick={() => { infoOverlayStore.set({ isOpen: true }) }} icon={Info} title='Details' variant='flat' size='large' />
        {props.menu}
      </Row>
      <OverlayContent title='Inhaltsverzeichnis' description={props.title} ref={parentRef}>
        <Box bleedLeft={18} bleedRight={18}>
          <TocContext.Provider value={{ ref: parentRef, onSelect }}>
            {props.children}
          </TocContext.Provider>
        </Box>
      </OverlayContent>
    </Overlay>
  )
}

export default BottomBar

interface LawMenuProps {
  risLink?: string
  findokLink?: string
}
export const LawMenu: FC<LawMenuProps> = function ({ risLink, findokLink }) {
  function handlePrint (): void {
    window.print()
  }

  return (
    <MenuList>
      <MenuItem icon={Printer} onClick={handlePrint}>
        Drucken/PDF
      </MenuItem>
      {risLink && (
        <MenuItem icon={ExternalLink} href={risLink} target='_blank'>
          Im RIS öffnen
        </MenuItem>
      )}
      {findokLink && (
        <MenuItem icon={ExternalLink} href={findokLink} target='_blank'>
          In Findok öffnen
        </MenuItem>
      )}
      <MenuItem icon={AlertTriangle} href='/kontakt' target='_blank'>
        Fehler melden
      </MenuItem>
    </MenuList>
  )
}
